/**
 * סל אופליין — כללים פשוטים:
 * 1) הוספה / עדכון כמות / הסרה → מיד במכשיר + ברקע לשרת
 * 2) שליחת הזמנה → מוחק את הפריטים במכשיר ובשרת
 * 3) סנכרון לא מוחק מהשרת דברים שחסרים במכשיר (רק פעולת הסרה מפורשת)
 * 4) סנכרון לא מחזיר לשרת פריטים ישנים מהמכשיר אחרי שהשרת כבר ניקה אותם
 *    (רק שינויים עם dirty=true — פעולת משתמש שממתינה להעלאה)
 */

import { logCartClient } from "@/lib/cartClientLog";
import { shouldPreferLocalData } from "@/lib/connectionQuality";

const PREFIX = "reshet_offline_cart_";
const DELETE_PREFIX = "reshet_offline_cart_deletes_";
/** ברשת טובה — סנכרון מהיר; ברשת חלשה — מחכים כדי לא לחנוק ניווט */
const CART_SYNC_DEBOUNCE_MS = 400;
const CART_SYNC_DEBOUNCE_SLOW_MS = 3500;

/** מזהה סניף יציב (מחרוזת) — אובייקטים/ ObjectId לא יוצרים מפתח localStorage שגוי */
export function normalizeStoreId(storeId) {
  if (storeId == null || storeId === "") return "";
  if (typeof storeId === "object") {
    const id = storeId._id ?? storeId.$oid ?? storeId.id;
    return id != null ? String(id) : "";
  }
  return String(storeId);
}

/** מזהה ענף יציב — ריק = סל ראשי של הסניף */
export function normalizeBranchId(branchId) {
  if (branchId == null || branchId === "") return "";
  if (typeof branchId === "object") {
    const id = branchId._id ?? branchId.$oid ?? branchId.id;
    return id != null ? String(id) : "";
  }
  return String(branchId);
}

function getKey(storeId, branchId) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return `${PREFIX}`;
  return bid ? `${PREFIX}${sid}__b_${bid}` : `${PREFIX}${sid}`;
}

function deleteKey(storeId, branchId) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return `${DELETE_PREFIX}`;
  return bid ? `${DELETE_PREFIX}${sid}__b_${bid}` : `${DELETE_PREFIX}${sid}`;
}

function productIdKey(item) {
  if (!item) return "";
  return String(item.product_id?._id ?? item.product_id?.id ?? item.product_id ?? "");
}

function readQty(item) {
  const n = Number(item?.quantity);
  return Number.isFinite(n) ? n : 0;
}

function getPendingDeletes(storeId, branchId) {
  const key = deleteKey(storeId, branchId);
  if (!normalizeStoreId(storeId) || typeof localStorage === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

function setPendingDeletes(storeId, ids, branchId) {
  const key = deleteKey(storeId, branchId);
  if (!normalizeStoreId(storeId) || typeof localStorage === "undefined") return;
  try {
    const list = [...ids];
    if (list.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(list));
  } catch {}
}

function addPendingDelete(storeId, productId, branchId) {
  const pid = String(productId || "");
  if (!pid) return;
  const set = getPendingDeletes(storeId, branchId);
  set.add(pid);
  setPendingDeletes(storeId, set, branchId);
}

function clearPendingDelete(storeId, productId, branchId) {
  const pid = String(productId || "");
  if (!pid) return;
  const set = getPendingDeletes(storeId, branchId);
  if (!set.delete(pid)) return;
  setPendingDeletes(storeId, set, branchId);
}

/**
 * מיזוג שרת ← מקומי:
 * - בסיס = מה שבשרת
 * - מחיקות ממתינות (הסרה אופליין) לא חוזרות מהשרת
 * - dirty מקומי: כמות מקומית גוברת / פריט חדש שנוסף באופליין נשמר
 * - פריט מקומי ישן בלי dirty שלא בשרת = נזרק (אחרי הזמנה/ניקוי)
 */
export function mergeServerCartWithOffline(serverList, offlineList, storeId = "", branchId = "") {
  const server = Array.isArray(serverList) ? serverList : [];
  const offline = Array.isArray(offlineList) ? offlineList : [];
  const pendingDeletes = storeId ? getPendingDeletes(storeId, branchId) : new Set();
  const merged = new Map();

  for (const item of server) {
    const k = productIdKey(item);
    if (!k || pendingDeletes.has(k)) continue;
    merged.set(k, item);
  }

  for (const item of offline) {
    const k = productIdKey(item);
    if (!k || pendingDeletes.has(k)) continue;
    const qty = readQty(item);
    if (qty <= 0) continue;

    const existing = merged.get(k);
    if (!existing) {
      // רק הוספה מקומית שממתינה להעלאה — לא "זומבים" ישנים
      if (item.dirty === true) merged.set(k, item);
      continue;
    }

    if (item.dirty === true && qty !== readQty(existing)) {
      merged.set(k, {
        ...existing,
        quantity: qty,
        supplier_id: item.supplier_id ?? existing.supplier_id,
        supplier_name: item.supplier_name ?? existing.supplier_name,
        dirty: true,
      });
    }
  }

  return Array.from(merged.values());
}

const pendingSyncByStore = new Map();
const syncTimerByStore = new Map();
const flushInFlightByStore = new Map();

function syncScopeKey(storeId, branchId) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  return bid ? `${sid}|${bid}` : sid;
}

function scheduleCartItemServerSync(storeId, item, deps) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(deps?.branchId ?? item?.branch_id);
  if (!sid || typeof navigator === "undefined" || !navigator.onLine) return;
  const pid = String(item.product_id?._id ?? item.product_id ?? "");
  if (!pid) return;
  const scope = syncScopeKey(sid, bid);

  if (!pendingSyncByStore.has(scope)) pendingSyncByStore.set(scope, new Map());
  pendingSyncByStore.get(scope).set(pid, { ...item, store_id: sid, branch_id: bid });

  if (flushInFlightByStore.get(scope)) return;

  if (syncTimerByStore.has(scope)) clearTimeout(syncTimerByStore.get(scope));
  const delay = shouldPreferLocalData() ? CART_SYNC_DEBOUNCE_SLOW_MS : CART_SYNC_DEBOUNCE_MS;
  syncTimerByStore.set(
    scope,
    setTimeout(() => {
      flushPendingCartSync(sid, bid, deps);
    }, delay)
  );
}

async function flushPendingCartSync(sid, branchId, deps) {
  const bid = normalizeBranchId(branchId);
  const scope = syncScopeKey(sid, bid);
  if (flushInFlightByStore.get(scope)) return;
  flushInFlightByStore.set(scope, true);
  syncTimerByStore.delete(scope);

  const { updateCartItem, fetchList } = deps;
  if (!updateCartItem) {
    flushInFlightByStore.delete(scope);
    return;
  }

  try {
    while (true) {
      const pending = pendingSyncByStore.get(scope);
      if (!pending?.size) break;
      const items = [...pending.values()];
      pending.clear();

      for (const item of items) {
        const productId = item.product_id?._id ?? item.product_id;
        if (!productId) continue;
        const pid = String(productId);
        const latest = pendingSyncByStore.get(scope)?.get(pid);
        if (latest) pendingSyncByStore.get(scope).delete(pid);
        const toSend = latest || item;
        const qty = readQty(toSend);
        const source = toSend._syncSource || "flushPendingCartSync";
        const reason =
          toSend._syncReason || (qty <= 0 ? "quantity_zero_sync" : "background_sync");
        try {
          await updateCartItem({
            product_id: productId,
            store_id: sid,
            branch_id: bid || undefined,
            quantity: qty,
            supplier_id: toSend.supplier_id ?? undefined,
            client_source: source,
            reason,
          });
          logCartClient(qty <= 0 ? "remove" : "update", {
            store_id: sid,
            product_id: productId,
            quantity_after: qty,
            source,
            reason,
            meta: { branch_id: bid || null },
          });
          if (!pendingSyncByStore.get(scope)?.has(pid)) {
            markOfflineCartItemClean(sid, productId, bid);
          }
        } catch (e) {
          console.warn("cart background sync failed", productId, e);
          if (!pendingSyncByStore.has(scope)) pendingSyncByStore.set(scope, new Map());
          if (!pendingSyncByStore.get(scope).has(pid)) {
            pendingSyncByStore.get(scope).set(pid, { ...toSend, store_id: sid, branch_id: bid });
          }
        }
      }
    }

  // ברשת חלשה — לא מושכים את כל הסל אחרי כל עדכון (בקשה כפולה שחונקת את האפליקציה)
  if (typeof fetchList === "function" && !shouldPreferLocalData()) {
    try {
      const list = await fetchList();
      const arr = Array.isArray(list) ? list : (list?.data ?? []);
      const local = getOfflineCart(sid, bid);
      const stillPending = pendingSyncByStore.get(scope);
      const offlinePending = stillPending ? [...stillPending.values()] : [];
      const merged = mergeServerCartWithOffline(arr, [...local, ...offlinePending], sid, bid);
      setOfflineCart(sid, merged, bid);
      logCartClient("merge", {
        store_id: sid,
        source: "flushPendingCartSync",
        meta: { server: arr.length, local: local.length, merged: merged.length, branch_id: bid || null },
      });
    } catch (e) {
      console.warn("cart background refresh failed", e);
    }
  }
  } finally {
    flushInFlightByStore.delete(scope);
    if (pendingSyncByStore.get(scope)?.size) {
      const delay = shouldPreferLocalData() ? CART_SYNC_DEBOUNCE_SLOW_MS : CART_SYNC_DEBOUNCE_MS;
      if (syncTimerByStore.has(scope)) clearTimeout(syncTimerByStore.get(scope));
      syncTimerByStore.set(
        scope,
        setTimeout(() => {
          flushPendingCartSync(sid, bid, deps);
        }, delay)
      );
    }
  }
}

/**
 * עדכון מיידי ב-localStorage + סנכרון לשרת ברקע (לא חוסם UI).
 * @returns {Array} רשימת הסל המעודכנת
 */
export function applyLocalCartChange(storeId, item, deps = {}) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(deps.branchId ?? item?.branch_id);
  const before = getOfflineCart(sid, bid);
  const pid = String(item.product_id?._id || item.product_id || "");
  const prev = before.find((i) => productIdKey(i) === pid);
  const next = addOrUpdateOfflineCartItem(storeId, { ...item, branch_id: bid, dirty: true });
  const qty = readQty(item);
  logCartClient(qty <= 0 ? "remove" : prev ? "update" : "add", {
    store_id: sid,
    product_id: pid,
    product_name: item.product?.name || item.product_id?.name || null,
    quantity_before: prev ? readQty(prev) : 0,
    quantity_after: Math.max(0, qty),
    source: "applyLocalCartChange",
    meta: { branch_id: bid || null },
  });
  scheduleCartItemServerSync(storeId, { ...item, branch_id: bid, dirty: true }, { ...deps, branchId: bid });
  return next;
}

/**
 * @param {string} storeId
 * @returns {Array<{ _id: string, product_id: object|string, quantity: number, store_id: string, supplier_id?: string }>}
 */
export function getOfflineCart(storeId, branchId = "") {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return [];
  try {
    const raw = localStorage.getItem(getKey(sid, bid));
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * @param {string} storeId
 * @param {Array} items
 */
export function setOfflineCart(storeId, items, branchId = "") {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return;
  try {
    localStorage.setItem(getKey(sid, bid), JSON.stringify(items || []));
  } catch (e) {
    console.warn("offlineCart.setOfflineCart failed", e);
  }
}

function markOfflineCartItemClean(storeId, productId, branchId = "") {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  const pid = String(productId || "");
  if (!sid || !pid) return;
  const list = getOfflineCart(sid, bid);
  let changed = false;
  const next = list.map((item) => {
    if (productIdKey(item) !== pid) return item;
    if (item.dirty) {
      changed = true;
      const { dirty, ...rest } = item;
      return rest;
    }
    return item;
  });
  if (changed) setOfflineCart(sid, next, bid);
}

/**
 * מוסיף או מעדכן פריט בסל האופליין. מחזיר את הרשימה המעודכנת.
 * שומר גם supplier_name כדי להציג את שם הספק באופליין.
 * @param {string} storeId
 * @param {{ product_id: string, quantity: number, store_id: string, supplier_id?: string, supplier_name?: string, product?: object, dirty?: boolean }} item
 * @returns {Array}
 */
export function addOrUpdateOfflineCartItem(storeId, item) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(item?.branch_id);
  const list = getOfflineCart(sid, bid);
  const productId = String(item.product_id?._id || item.product_id);
  const idx = list.findIndex(
    (i) => String(i.product_id?._id || i.product_id) === productId
  );
  const supplierName = item.supplier_name ?? item.supplier?.name ?? null;
  // אל תשתמש ב־|| 1 — 0 חייב להישאר 0 כדי להסיר פריט מהסל
  const parsedQty = Number(item.quantity);
  const nextQuantity = Number.isFinite(parsedQty) ? parsedQty : 1;
  const prevServerId =
    idx >= 0 && list[idx]._id && !String(list[idx]._id).startsWith("offline-")
      ? list[idx]._id
      : null;
  const newItem = {
    _id: prevServerId || `offline-${productId}`,
    product_id: item.product || item.product_id,
    quantity: nextQuantity,
    store_id: normalizeStoreId(item.store_id) || sid,
    branch_id: bid,
    supplier_id: item.supplier_id ?? null,
    supplier_name: supplierName != null ? supplierName : (idx >= 0 ? list[idx].supplier_name : null),
    dirty: item.dirty === true ? true : idx >= 0 ? !!list[idx].dirty : false,
  };
  if (idx >= 0) {
    if (newItem.quantity <= 0) {
      list.splice(idx, 1);
    } else {
      list[idx] = { ...list[idx], ...newItem };
    }
  } else if (newItem.quantity > 0) {
    list.push(newItem);
  }
  if (newItem.quantity > 0) clearPendingDelete(sid, productId, bid);
  else addPendingDelete(sid, productId, bid);
  setOfflineCart(sid, list, bid);
  return list;
}

/**
 * @param {string} storeId
 * @param {string} productId
 * @param {{ source?: string, reason?: string, product_name?: string, skipLog?: boolean }} [meta]
 */
export function removeOfflineCartItem(storeId, productId, meta = {}) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(meta.branchId);
  const pid = String(productId || "");
  const list = getOfflineCart(sid, bid);
  const existing = list.find((i) => String(i.product_id?._id || i.product_id) === pid);
  const next = list.filter((i) => String(i.product_id?._id || i.product_id) !== pid);
  setOfflineCart(sid, next, bid);

  // הסרה מפורשת / הזמנה — לא לתת לסנכרון להחזיר את הפריט מהשרת
  const reason = meta.reason || "local_remove";
  if (!meta.skipPendingDelete) {
    addPendingDelete(sid, pid, bid);
  }

  if (!meta.skipLog && (existing || meta.forceLog)) {
    logCartClient("remove", {
      store_id: sid,
      product_id: pid,
      product_name:
        meta.product_name ||
        existing?.product_id?.name ||
        existing?.product?.name ||
        null,
      quantity_before: existing ? readQty(existing) : null,
      quantity_after: 0,
      source: meta.source || "removeOfflineCartItem",
      reason,
      meta: meta.cart_item_id ? { cart_item_id: meta.cart_item_id } : undefined,
    });
  }
  return next;
}

/** מוחק מהסל המקומי את כל פריטי הספק (אחרי שליחת הזמנה) */
export function removeOfflineCartBySupplier(storeId, supplierId, meta = {}) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(meta.branchId);
  const sup = String(supplierId?._id || supplierId || "");
  if (!sid || !sup) return getOfflineCart(sid, bid);
  const list = getOfflineCart(sid, bid);
  const kept = [];
  let removed = 0;
  for (const item of list) {
    const itemSup = String(item.supplier_id?._id || item.supplier_id || "");
    if (itemSup && itemSup === sup) {
      removed += 1;
      addPendingDelete(sid, productIdKey(item), bid);
      continue;
    }
    kept.push(item);
  }
  setOfflineCart(sid, kept, bid);
  logCartClient("clear", {
    store_id: sid,
    source: meta.source || "removeOfflineCartBySupplier",
    reason: meta.reason || "order_sent",
    meta: { supplier_id: sup, removed },
  });
  return kept;
}

/**
 * מסיר פריט מהסל המקומי ומסנכרן לשרת כשיש רשת.
 * זו מחיקה מפורשת בלבד — לא רצה בסנכרון אוטומטי.
 */
export async function persistCartRemoval(storeId, itemOrProductId, deps = {}) {
  const sid = normalizeStoreId(storeId);
  let productId = "";
  let cartItemId = null;
  let productName = null;
  if (itemOrProductId && typeof itemOrProductId === "object") {
    productId = String(itemOrProductId.product_id?._id ?? itemOrProductId.product_id ?? "");
    cartItemId = itemOrProductId._id ?? null;
    productName =
      itemOrProductId.product_id?.name ||
      itemOrProductId.product?.name ||
      null;
  } else {
    productId = String(itemOrProductId ?? "").replace(/^offline-/, "");
  }

  const bid = normalizeBranchId(deps.branchId);
  const next = removeOfflineCartItem(sid, productId, {
    source: "persistCartRemoval",
    reason: "user_explicit_remove",
    product_name: productName,
    cart_item_id: cartItemId,
    branchId: bid,
  });
  if (typeof navigator !== "undefined" && navigator.onLine && sid && productId) {
    const { deleteCartItem, updateCartItem } = deps;
    try {
      if (cartItemId && !String(cartItemId).startsWith("offline-") && deleteCartItem) {
        await deleteCartItem(cartItemId, {
          reason: "user_explicit_remove",
          client_source: "persistCartRemoval",
        });
      } else if (updateCartItem) {
        await updateCartItem({
          product_id: productId,
          store_id: sid,
          branch_id: bid || undefined,
          quantity: 0,
          reason: "user_explicit_remove",
          client_source: "persistCartRemoval",
        });
      }
      clearPendingDelete(sid, productId, bid);
    } catch (e) {
      console.warn("persistCartRemoval server sync failed", productId, e);
    }
  }
  return next;
}

/**
 * מעדכן פריט בסל המקומי ומסנכרן לשרת כשיש רשת.
 */
export async function persistCartUpdate(storeId, item, deps = {}) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(deps.branchId ?? item?.branch_id);
  const pid = String(item.product_id?._id || item.product_id || "");
  const prev = getOfflineCart(sid, bid).find((i) => productIdKey(i) === pid);
  const qty = readQty(item);
  const next = addOrUpdateOfflineCartItem(sid, { ...item, branch_id: bid, dirty: true });

  logCartClient(qty <= 0 ? "remove" : prev ? "update" : "add", {
    store_id: sid,
    product_id: pid,
    product_name: item.product?.name || item.product_id?.name || null,
    quantity_before: prev ? readQty(prev) : 0,
    quantity_after: Math.max(0, qty),
    source: "persistCartUpdate",
    meta: { branch_id: bid || null },
  });

  if (sid && deps.updateCartItem) {
    scheduleCartItemServerSync(
      storeId,
      {
        ...item,
        branch_id: bid,
        dirty: true,
        _syncSource: "persistCartUpdate",
        _syncReason: qty <= 0 ? "user_explicit_remove" : "user_quantity_update",
      },
      { ...deps, branchId: bid }
    );
  }
  return next;
}

/**
 * מנקה את סל האופליין אחרי סנכרון מוצלח.
 * @param {string} storeId
 */
export function clearOfflineCart(storeId, branchId = "") {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return;
  try {
    localStorage.removeItem(getKey(sid, bid));
    setPendingDeletes(sid, new Set(), bid);
    logCartClient("clear", { store_id: sid, source: "clearOfflineCart", meta: { branch_id: bid || null } });
  } catch {}
}

export function isNetworkError(err) {
  if (!err) return false;
  const msg = (err.message || "").toLowerCase();
  const code = err?.code ?? err?.errno;
  return (
    msg.includes("network") ||
    msg.includes("failed to fetch") ||
    msg.includes("internet") ||
    msg.includes("timeout") ||
    msg.includes("abort") ||
    code === "ERR_NETWORK" ||
    code === "ECONNABORTED" ||
    code === "ERR_CANCELED" ||
    code === "ABORT_ERR"
  );
}

/**
 * מחזיר את רשימת הסל: מהשרת כשמחוברים, מהמטמון המקומי כשמתחת.
 * לשימוש ב־queryFn: () => getCartItemsWithOffline(storeId)
 */
export async function getCartItemsWithOffline(storeId, apiListFn, branchId = "", options = {}) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  const forceServer = !!options?.forceServer;
  if (!sid) return [];
  const offline = getOfflineCart(sid, bid);
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return offline;
  }
  // 3G / קליטה גרועה — מציגים מיד את הסל המקומי; לא מחכים ל־15s timeout מהשרת
  // בשידור הזמנה (forceServer) חובה למשוך מהשרת — אחרת נשלח סל מקומי חלקי
  if (!forceServer && shouldPreferLocalData()) {
    return offline;
  }
  try {
    const data = await apiListFn();
    const list = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
    const merged = mergeServerCartWithOffline(list, offline, sid, bid);
    setOfflineCart(sid, merged, bid);
    logCartClient("merge", {
      store_id: sid,
      source: "getCartItemsWithOffline",
      meta: { server: list.length, local: offline.length, merged: merged.length, branch_id: bid || null },
    });
    return merged;
  } catch (e) {
    if (isNetworkError(e)) return offline.length ? offline : getOfflineCart(sid, bid);
    throw e;
  }
}

/**
 * מושך מהשרת ומתמזג למקומי — בלי להחזיר זומבים ובלי מחיקות אוטומטיות מהשרת.
 */
export async function pullServerCartIntoLocal(storeId, fetchList, branchId = "") {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid || typeof fetchList !== "function") return getOfflineCart(sid, bid);
  const list = await fetchList();
  const serverItems = Array.isArray(list) ? list : (list?.data ?? []);
  const local = getOfflineCart(sid, bid);
  const merged = mergeServerCartWithOffline(serverItems, local, sid, bid);
  setOfflineCart(sid, merged, bid);
  logCartClient("merge", {
    store_id: sid,
    source: "pullServerCartIntoLocal",
    meta: { server: serverItems.length, local: local.length, merged: merged.length, branch_id: bid || null },
  });
  return merged;
}

/**
 * סנכרון אחרי חזרת רשת:
 * - דוחף רק dirty (שינויי משתמש)
 * - דוחף מחיקות ממתינות
 * - לא מוחק מהשרת פריטים שפשוט חסרים במכשיר
 * - לא מחזיר לשרת פריטים ישנים בלי dirty
 */
export async function syncOfflineCartToServer(storeId, { queryClient, updateCartItem, deleteCartItem, fetchList, branchId = "" }) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return;

  // 1) משיכה + מיזוג נקי
  let serverItems = [];
  if (typeof fetchList === "function") {
    try {
      const list = await fetchList();
      serverItems = Array.isArray(list) ? list : (list?.data ?? []);
      const localBefore = getOfflineCart(sid, bid);
      const mergedPull = mergeServerCartWithOffline(serverItems, localBefore, sid, bid);
      setOfflineCart(sid, mergedPull, bid);
      logCartClient("sync", {
        store_id: sid,
        source: "syncOfflineCartToServer",
        reason: "pull_merge_before_push",
        meta: {
          server: serverItems.length,
          local_before: localBefore.length,
          merged: mergedPull.length,
          pending_deletes: getPendingDeletes(sid, bid).size,
          branch_id: bid || null,
        },
      });
    } catch (e) {
      console.warn("offlineCart fetch before sync failed", e);
    }
  }

  await pushOfflineCartChanges(sid, bid, {
    updateCartItem,
    deleteCartItem,
    serverItemsHint: serverItems,
  });

  // 4) רענון סופי מהשרת
  if (typeof fetchList === "function") {
    try {
      const list = await fetchList();
      const arr = Array.isArray(list) ? list : (list?.data ?? []);
      const offlineAfter = getOfflineCart(sid, bid);
      const mergedFinal = mergeServerCartWithOffline(arr, offlineAfter, sid, bid);
      setOfflineCart(sid, mergedFinal, bid);
      if (queryClient) {
        queryClient.setQueryData(["cartItems", sid, bid || "main"], mergedFinal);
      }
      logCartClient("sync", {
        store_id: sid,
        source: "syncOfflineCartToServer",
        reason: "final_pull_merge",
        meta: { server: arr.length, merged: mergedFinal.length, branch_id: bid || null },
      });
    } catch (e) {
      console.warn("offlineCart final fetch failed", e);
    }
  }
}

/**
 * ברשת חלשה: רק מעלה dirty + מחיקות ממתינות — בלי משיכת סל מלאה מהשרת.
 * חשוב: לא מדלגים על זה גם ב־3G, כדי שמוצרים שנוספו אופליין לא יישארו רק במכשיר.
 */
export async function pushOfflineCartDirtyOnly(storeId, { updateCartItem, deleteCartItem, fetchList, queryClient, branchId = "" } = {}) {
  const sid = normalizeStoreId(storeId);
  const bid = normalizeBranchId(branchId);
  if (!sid) return;

  let serverItems = [];
  // ניסיון קצר למשיכה — אם נכשל ממשיכים לדחוף לפי המקומי
  if (typeof fetchList === "function" && typeof navigator !== "undefined" && navigator.onLine) {
    try {
      const list = await fetchList();
      serverItems = Array.isArray(list) ? list : (list?.data ?? []);
      const localBefore = getOfflineCart(sid, bid);
      const mergedPull = mergeServerCartWithOffline(serverItems, localBefore, sid, bid);
      setOfflineCart(sid, mergedPull, bid);
      if (queryClient) {
        queryClient.setQueryData(["cartItems", sid, bid || "main"], mergedPull);
      }
    } catch (e) {
      console.warn("offlineCart dirty-only pull skipped", e?.message || e);
    }
  }

  await pushOfflineCartChanges(sid, bid, {
    updateCartItem,
    deleteCartItem,
    serverItemsHint: serverItems,
  });

  if (queryClient) {
    queryClient.setQueryData(["cartItems", sid, bid || "main"], getOfflineCart(sid, bid));
  }
}

async function pushOfflineCartChanges(sid, bid, { updateCartItem, deleteCartItem, serverItemsHint = [] }) {
  const serverItems = Array.isArray(serverItemsHint) ? serverItemsHint : [];

  // מחיקות ממתינות
  const pendingDeletes = [...getPendingDeletes(sid, bid)];
  for (const pid of pendingDeletes) {
    if (!pid) continue;
    try {
      const onServer = serverItems.find((i) => productIdKey(i) === pid);
      if (onServer?._id && deleteCartItem && !String(onServer._id).startsWith("offline-")) {
        await deleteCartItem(onServer._id, {
          reason: "pending_local_delete",
          client_source: "pushOfflineCartChanges",
        });
      } else if (updateCartItem) {
        await updateCartItem({
          product_id: pid,
          store_id: sid,
          branch_id: bid || undefined,
          quantity: 0,
          reason: "pending_local_delete",
          client_source: "pushOfflineCartChanges",
        });
      }
      clearPendingDelete(sid, pid, bid);
      logCartClient("remove", {
        store_id: sid,
        product_id: pid,
        quantity_after: 0,
        source: "pushOfflineCartChanges",
        reason: "pending_local_delete",
      });
    } catch (e) {
      console.warn("offlineCart pending delete failed", pid, e);
    }
  }

  // דחיפת dirty בלבד
  if (!updateCartItem) return;
  const localItems = getOfflineCart(sid, bid);
  for (const item of localItems) {
    if (item.dirty !== true) continue;
    const productId = item.product_id?._id ?? item.product_id;
    if (!productId) continue;
    const pid = String(productId);
    const qty = readQty(item);
    if (qty <= 0) continue;

    try {
      await updateCartItem({
        product_id: productId,
        store_id: sid,
        branch_id: bid || undefined,
        quantity: qty,
        supplier_id: item.supplier_id ?? undefined,
        client_source: "pushOfflineCartChanges",
        reason: "push_dirty_item",
      });
      logCartClient("update", {
        store_id: sid,
        product_id: pid,
        quantity_after: qty,
        source: "pushOfflineCartChanges",
        reason: "push_dirty_item",
      });
      markOfflineCartItemClean(sid, pid, bid);
      clearPendingDelete(sid, pid, bid);
    } catch (e) {
      console.warn("offlineCart sync item failed", productId, e);
    }
  }
}
